import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useSearchParams} from 'react-router-dom';
import {apiErrorMessage} from '../api/abfApi';
import {StatusChip} from '../components/StatusChip';
import {DateField} from '../../../components/shared/DateField';
import {Button, INK} from '../onboarding/review/reviewStyles';
import {SmallButton} from '../onboarding/review/contractReviewStyles';
import {overviewApi, type Offer} from './forwardFlowOverviewApi';
import {servicingApi, type ServicingOverview} from './forwardFlowServicingApi';
import {EvidenceField, Fields, GatedAction, Grid, Row, Scroller, Section, useCommand, useFeedback, useViewer, type StoredEvidence} from './ForwardFlowShared';
import {count, day, moment} from './forwardFlowWords';

const root = '/api/credit/abf/forward-flow/programs';

type Report = {
  id: number; report_kind: string; subject_id: number | null; as_of: string; report_version: number;
  manifest_sha256: string; basis: string; state: 'IN_FORCE' | 'SUPERSEDED'; restates_id: number | null;
  rendered_by: string; rendered_at: string; deliveries: number;
};
type Delivery = {
  id: number; recipient: string; channel: string; delivered_on: string; acknowledgement: string;
  acknowledgement_note?: string | null; recorded_by: string;
};
type Opened = Omit<Report, 'deliveries'> & {deliveries: Delivery[]};

const KINDS: Array<{kind: string; label: string; needs: 'purchase' | 'period' | null; about: string}> = [
  {kind: 'PURCHASE_CERTIFICATE', label: 'Purchase certificate', needs: 'purchase',
    about: 'One approved purchase: the loans, their trades and the payments to the seller.'},
  {kind: 'SERVICING_CLOSE', label: 'Servicing close report', needs: 'period',
    about: 'One signed-off period: the statement, what was booked, the remittances and the differences.'},
  {kind: 'CLAIMS_OBLIGATIONS', label: 'Claims and amounts owed', needs: null,
    about: 'Seller claims, what each side owes and any stop in force, as of today.'},
  {kind: 'ASSET_RECONCILIATION', label: 'Loans against the official book', needs: null,
    about: 'Every loan purchased against its position on the official book today.'},
];
const kindLabel = (kind: string) => KINDS.find(k => k.kind === kind)?.label ?? kind.replace(/_/g, ' ').toLowerCase();
const CHANNEL: Record<string, string> = {EXPORT: 'Downloaded to hand over', RECORDED_EXTERNAL: 'Sent outside the system'};
const ACK: Record<string, {label: string; tone: 'done' | 'warning' | 'critical'}> = {
  PENDING: {label: 'Receipt not yet confirmed', tone: 'warning'}, ACKNOWLEDGED: {label: 'Receipt confirmed', tone: 'done'},
  MISMATCH: {label: 'Recipient reported a mismatch', tone: 'critical'},
};

/**
 * Reports: a report is produced from the records as they stand and never changes afterwards.
 * If the records change, producing it again makes a new version that replaces the old one,
 * which is kept. Delivery is recorded here; nothing is sent from this screen.
 */
export function ForwardFlowReportingWorkspace({programId}: {programId: number}) {
  const viewer = useViewer();
  const notify = useFeedback();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = Number(searchParams.get('report')) || null;
  const [reports, setReports] = useState<Report[] | null>(null);
  const [periods, setPeriods] = useState<ServicingOverview['periods']>([]);
  const [purchases, setPurchases] = useState<Offer[]>([]);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [subject, setSubject] = useState<Record<string, string>>({});
  const [delivery, setDelivery] = useState<{recipient: string; channel: string; deliveredOn: string} | null>(null);
  const [evidence, setEvidence] = useState<StoredEvidence | null>(null);
  const [mismatch, setMismatch] = useState<{deliveryId: number; note: string} | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, o, offers] = await Promise.all([
        axios.get<Report[]>(`${root}/${programId}/reports`).then(x => x.data),
        servicingApi.overview(programId).catch(() => null), overviewApi.offers(programId).catch(() => [] as Offer[])]);
      setReports(r); setPeriods((o?.periods ?? []).filter(p => p.state === 'SIGNED_OFF'));
      setPurchases(offers.filter(x => x.decisionState === 'APPROVED'));
      setOpened(openId ? await axios.get<Opened>(`${root}/${programId}/reports/${openId}`).then(x => x.data) : null);
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, 'The reports could not be loaded.')}); }
  }, [programId, openId, notify]);
  useEffect(() => { void load(); }, [load]);
  const {busy, run} = useCommand(load);

  const open = (id: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('report', String(id)); else next.delete('report');
    setSearchParams(next); setDelivery(null); setMismatch(null);
  };
  const subjectWords = (r: {report_kind: string; subject_id: number | null}) => {
    if (r.subject_id == null) return 'The whole program';
    if (r.report_kind === 'PURCHASE_CERTIFICATE') return purchases.find(p => p.decisionId === r.subject_id)?.sellerReference ?? 'A purchase';
    if (r.report_kind === 'SERVICING_CLOSE') return periods.find(p => p.id === r.subject_id)?.label ?? 'A servicing period';
    return 'The whole program';
  };
  const download = async (r: Opened) => {
    try {
      const blob = await axios.get(`${root}/${programId}/reports/${r.id}/rendering`, {responseType: 'blob'}).then(x => x.data);
      const url = URL.createObjectURL(blob); const a = document.createElement('a');
      a.href = url; a.download = `${kindLabel(r.report_kind).replace(/\s+/g, '-').toLowerCase()}-v${r.report_version}.json`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, 'The report could not be downloaded.')}); }
  };

  return <>
    {viewer.maker && <Section>
      <header><div><h2>Produce a report</h2>
        <p>A report is fixed at the moment it is produced. Producing the same report again changes nothing unless the records changed, in which case it becomes the next version.</p></div></header>
      <Fields $cols={4}>{KINDS.map(k => {
        const options = k.needs === 'purchase' ? purchases.map(p => [String(p.decisionId), `${p.sellerReference} · ${count(p.totalLoans, 'loan')}`])
          : k.needs === 'period' ? periods.map(p => [String(p.id), p.label]) : [];
        const none = k.needs && options.length === 0;
        return <div key={k.kind} style={{border: `1px solid ${INK.line}`, borderRadius: 10, padding: 14, background: INK.raised, display: 'grid', gap: 9, alignContent: 'start'}}>
          <strong style={{color: INK.strong, fontSize: 14}}>{k.label}</strong>
          <span style={{color: INK.muted, fontSize: 12, lineHeight: 1.5}}>{k.about}</span>
          {k.needs && !none && <label>{k.needs === 'purchase' ? 'Purchase' : 'Servicing period'}
            <select value={subject[k.kind] ?? ''} onChange={e => setSubject({...subject, [k.kind]: e.target.value})}>
              <option value="">Choose…</option>{options.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>}
          <GatedAction busy={busy} reason={none ? (k.needs === 'purchase' ? 'No purchase has been approved yet.' : 'No servicing period has been signed off yet.')
              : k.needs && !subject[k.kind] ? `Choose the ${k.needs === 'purchase' ? 'purchase' : 'servicing period'}.` : null}
            onClick={() => void run(() => axios.post(`${root}/${programId}/reports`, {kind: k.kind, subjectId: k.needs ? Number(subject[k.kind]) : null}).then(x => x.data),
              r => r?.unchanged || r?.duplicate ? `${k.label}: nothing has changed since it was last produced.` : `${k.label} produced.`)}>Produce</GatedAction>
        </div>;
      })}</Fields>
    </Section>}

    <Section>
      <header><div><h2>Reports produced</h2><p>Select a report to download it or to record that it was delivered.</p></div></header>
      {reports === null ? <p>Loading…</p> : reports.length === 0 ? <p>No report has been produced yet.</p>
        : <Scroller><Grid>
          <thead><tr><th>Report</th><th>About</th><th>As of</th><th>Version</th><th>Status</th><th>Produced</th><th className="num">Deliveries</th></tr></thead>
          <tbody>{reports.map(r => <tr key={r.id} data-selected={openId === r.id}>
            <td><button className="link" onClick={() => open(openId === r.id ? null : r.id)}>{kindLabel(r.report_kind)}</button></td>
            <td>{subjectWords(r)}</td><td>{day(r.as_of)}</td>
            <td>Version {r.report_version}{r.restates_id ? <div className="muted" style={{fontSize: 12}}>replaces an earlier version</div> : null}</td>
            <td><StatusChip tone={r.state === 'IN_FORCE' ? 'done' : 'neutral'} label={r.state === 'IN_FORCE' ? 'Current' : 'Replaced'} /></td>
            <td>{r.rendered_by} · {moment(r.rendered_at)}</td><td className="num">{r.deliveries}</td>
          </tr>)}</tbody></Grid></Scroller>}

      {opened && <div style={{borderTop: `1px solid ${INK.line}`, marginTop: 16, paddingTop: 16}}>
        <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
          <h3 style={{margin: 0}}>{kindLabel(opened.report_kind)} · {subjectWords(opened)} · version {opened.report_version}</h3>
          <StatusChip tone={opened.state === 'IN_FORCE' ? 'done' : 'neutral'} label={opened.state === 'IN_FORCE' ? 'Current' : 'Replaced by a later version'} size="md" /></Row>
        <p style={{margin: '6px 0 4px'}}>{opened.basis}</p>
        <details style={{margin: '0 0 12px', fontSize: 12, color: INK.faint, overflowWrap: 'anywhere'}}>
          <summary style={{cursor: 'pointer'}}>Fingerprint of the report's exact content</summary>SHA-256 {opened.manifest_sha256}</details>
        <Row>
          <Button type="button" onClick={() => void download(opened)}>Download the report</Button>
          {viewer.maker && opened.state === 'IN_FORCE' && !delivery && <SmallButton type="button"
            onClick={() => { setDelivery({recipient: '', channel: 'EXPORT', deliveredOn: ''}); setEvidence(null); }}>Record a delivery…</SmallButton>}
        </Row>

        {delivery && <div style={{marginTop: 14}}>
          <Fields $cols={4}>
            <label>Delivered to<input value={delivery.recipient} onChange={e => setDelivery({...delivery, recipient: e.target.value})} placeholder="Name or organisation" /></label>
            <label>How<select value={delivery.channel} onChange={e => setDelivery({...delivery, channel: e.target.value})}>
              {Object.entries(CHANNEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
            <label>Delivered on<DateField value={delivery.deliveredOn} onChange={v => setDelivery({...delivery, deliveredOn: v})} aria-label="Delivered on" /></label>
            {delivery.channel === 'RECORDED_EXTERNAL' && <EvidenceField programId={programId} label="Proof it was sent" hint="The sent email or the courier receipt, as a PDF."
              value={evidence} onChange={setEvidence} />}
          </Fields>
          <Row><GatedAction variant="primary" busy={busy}
              reason={!delivery.recipient.trim() ? 'Say who it was delivered to.' : !delivery.deliveredOn ? 'Enter the date it was delivered.'
                : delivery.channel === 'RECORDED_EXTERNAL' && !evidence ? 'Attach the proof that it was sent.' : null}
              onClick={() => void run(() => axios.post(`${root}/${programId}/reports/${opened.id}/deliveries`, {...delivery, recipient: delivery.recipient.trim(),
                evidenceRef: evidence?.fileName, evidenceSha256: evidence?.sourceSha256}).then(() => setDelivery(null)), 'Delivery recorded.')}>Record the delivery</GatedAction>
            <SmallButton type="button" onClick={() => setDelivery(null)}>Cancel</SmallButton></Row>
        </div>}

        {opened.deliveries.length > 0 && <Grid style={{maxWidth: 900, marginTop: 14}}>
          <thead><tr><th>Delivered to</th><th>How</th><th>On</th><th>Receipt</th><th></th></tr></thead>
          <tbody>{opened.deliveries.map(d => <tr key={d.id}>
            <td>{d.recipient}</td><td>{CHANNEL[d.channel] ?? d.channel}</td><td>{day(d.delivered_on)}</td>
            <td><StatusChip tone={(ACK[d.acknowledgement] ?? ACK.PENDING).tone} label={(ACK[d.acknowledgement] ?? ACK.PENDING).label} />
              {d.acknowledgement_note ? <div className="muted" style={{fontSize: 12}}>{d.acknowledgement_note}</div> : null}</td>
            <td>{viewer.maker && d.acknowledgement === 'PENDING' && <Row>
              <SmallButton type="button" disabled={busy} onClick={() => void run(() => axios.post(
                `${root}/${programId}/reports/${opened.id}/deliveries/${d.id}/acknowledge`, {outcome: 'ACKNOWLEDGED'}), `Receipt by ${d.recipient} confirmed.`)}>Recipient confirmed receipt</SmallButton>
              <SmallButton type="button" disabled={busy} onClick={() => setMismatch({deliveryId: d.id, note: ''})}>Recipient reported a mismatch…</SmallButton>
            </Row>}</td>
          </tr>)}</tbody></Grid>}

        {mismatch && <div style={{marginTop: 12}}>
          <Fields $cols={1}><label>What the recipient says does not match
            <textarea value={mismatch.note} onChange={e => setMismatch({...mismatch, note: e.target.value})} /></label></Fields>
          <Row><GatedAction variant="primary" busy={busy} reason={!mismatch.note.trim() ? 'Say what does not match.' : null}
              onClick={() => void run(() => axios.post(`${root}/${programId}/reports/${opened.id}/deliveries/${mismatch.deliveryId}/acknowledge`,
                {outcome: 'MISMATCH', note: mismatch.note.trim()}).then(() => setMismatch(null)), 'Mismatch recorded.')}>Record the mismatch</GatedAction>
            <SmallButton type="button" onClick={() => setMismatch(null)}>Cancel</SmallButton></Row>
        </div>}
      </div>}
    </Section>
  </>;
}
